import { HttpError } from '@whistler-ui/k2/types/common';

import { requestWithOptions } from './helper';

const reportingBasePath =
  import.meta.env.REACT_APP_API_ENDPOINT_REPORTING || '/';

// Log searches have no server-side guarantee of ever answering; without a
// client-side abort a hanging request keeps TanStack Query's isFetching true
// forever and the whole Device Logs search form stays disabled.
//
// It sits deliberately ABOVE nginx's own 30s so the backend always answers
// first. Aborting at 30s too would race it and throw away a search that was
// about to return rows.
export const SEARCH_LOGS_TIMEOUT_MS = 35_000;

export const SEARCH_LOGS_TIMEOUT_MESSAGE =
  'The search request timed out. Please try again.';

// Runs a request with an abort-on-timeout signal. A timeout is rethrown with
// a stable, user-readable message marked as user-facing (the raw DOMException
// message differs per browser and is hidden from the UI).
const withSearchTimeout = async <T>(
  run: (signal: AbortSignal) => Promise<T>,
): Promise<T> => {
  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, SEARCH_LOGS_TIMEOUT_MS);

  try {
    return await run(controller.signal);
  } catch (error) {
    if (timedOut) {
      throw new HttpError(SEARCH_LOGS_TIMEOUT_MESSAGE, undefined, {
        userMessage: SEARCH_LOGS_TIMEOUT_MESSAGE,
      });
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * @public
 * This search will be available for "regular" users.
 */

type Params = {
  environmentId: string;
  searchParams: Record<string, any>;
};

type ApiLogsParams = Pick<Params, 'environmentId' | 'searchParams'> & {
  endpointIds: string[];
};

export const searchCarrierApiLogs = async <T = any>(
  params: ApiLogsParams,
): Promise<T> => {
  const { environmentId, searchParams, endpointIds } = params;

  // Build the query string for endpointIds if provided
  const endpointIdQuery =
    endpointIds && endpointIds.length > 0
      ? endpointIds
          .map(endpoint => `endpoint_id=${encodeURIComponent(endpoint)}`)
          .join('&')
      : '';

  // Build the search params from the provided searchParams object
  const urlParams = new URLSearchParams({
    ...searchParams,
  }).toString();

  // Construct the final API endpoint path
  const basePath = `${reportingBasePath}/logs/${environmentId}/carrier-api`;
  // Combine search params and endpointId query parts, filtering out any empty strings
  const queryString = [urlParams, endpointIdQuery]
    .filter(Boolean) // removes empty strings
    .join('&');
  const url = `${basePath}?${queryString}`;

  // Make the GET request with the constructed URL, aborting on timeout
  const response = await withSearchTimeout(signal =>
    requestWithOptions(url, { method: 'GET', signal }),
  );
  return response;
};

/**
 * @private
 * This search will be available only for netlync.
 */

type DeviceLogsParams = Pick<Params, 'environmentId' | 'searchParams'> & {
  appIds?: string[];
  requestActions?: string[];
};

export const searchInterDeviceLogs = async <T = any>(
  params: DeviceLogsParams,
): Promise<T> => {
  const { searchParams, environmentId, appIds, requestActions } = params;

  // Build the query string for appIds if provided
  const appIdQuery =
    appIds && appIds.length > 0
      ? appIds.map(appId => `app_id=${encodeURIComponent(appId)}`).join('&')
      : '';

  // Build the query string for requestActions if provided
  const requestActionsQuery =
    requestActions && requestActions.length > 0
      ? requestActions
          .map(action => `request_actions=${encodeURIComponent(action)}`)
          .join('&')
      : '';

  // Build the search params from the provided searchParams object
  const urlParams = new URLSearchParams({
    ...searchParams,
  }).toString();

  // Build the final API endpoint path
  const basePath = `${reportingBasePath}/logs/${environmentId}/device`;

  // Combine search params, appId query, and requestActions query parts, filtering out any empty strings
  const queryString = [urlParams, appIdQuery, requestActionsQuery]
    .filter(Boolean) // removes empty strings
    .join('&');

  const url = `${basePath}?${queryString}`;

  // Make the GET request with the constructed URL, aborting on timeout
  const response = await withSearchTimeout(signal =>
    requestWithOptions(url, { method: 'GET', signal }),
  );
  return response;
};

/**
 * @private
 * This search will be available only for netlync.
 */
export const searchInterAllLogs = async <T = any>(
  environmentId: string,
  searchParams: Record<string, any> = {},
): Promise<T> => {
  const urlParams = new URLSearchParams(searchParams).toString();
  const path = new URL(
    `${reportingBasePath}/intern/${environmentId}/all/logs?${urlParams}`,
  ).toString();
  const response = await requestWithOptions(path, { method: 'GET' });
  return response;
};

export const getDevicesRequestActions = async <T = any>(): Promise<T> => {
  const path = new URL(`${reportingBasePath}/device/actions`);

  const response = await requestWithOptions(path, { method: 'GET' });
  return response;
};
